// src/HOSTING/PLATFORMMANAGED/CLEANING/CREATE/CleaningForm.tsx
// ─────────────────────────────────────────────────────────────────────────────
// Multi-step cleaning listing creation form
// Steps: Categories → Address → Media → Pricing → Description → Settings
// Green theme · Montserrat + Cormorant Garamond · desktop sidebar + mobile bar
// ─────────────────────────────────────────────────────────────────────────────

import React, { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../../redux/store";
import {
  upsertCleaning,
  upsertCleaningMedia,
  listenToCleaningData,
} from "../../../../redux/cleaningSlice";
import {
  calculateCleaningGuestServiceFee,
} from "../../../../redux/cleaningFeeSlice";
import { cleaningAmenities, cleaningCategories, cleaningRulesCategories, cleaningSafetyCategories } from "../../../../CATEGORIES/Categories";

import {
  Check, ChevronLeft, ChevronRight, ArrowRight,
  Plus, X, RefreshCw, AlertCircle, MapPin,
  Clock, Calendar, User, Users, Sigma,
} from "lucide-react";
import { CircularProgress } from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import {
  Zap, UserCheck, Settings2, ShieldCheck, DollarSign,
} from "lucide-react";

declare global { interface Window { google: any; } }

/* ─── Tokens ──────────────────────────────────────────────── */
const G = {
  bg:           "#f0fdf4",
  surface:      "#ffffff",
  border:       "#bbf7d0",
  border2:      "#86efac",
  text:         "#14532d",
  textSoft:     "#166534",
  muted:        "#6b7280",
  sidebar:      "#14532d",
  sidebarHover: "#166534",
  sidebarActive:"#15803d",
  sidebarBorder:"#166534",
  green:        "#16a34a",
  greenLight:   "#dcfce7",
  greenDark:    "#14532d",
  gold:         "#4ade80",
  accentFg:     "#f0fdf4",
  rouge:        "#dc2626",
  sans:         "'Montserrat', sans-serif",
  serif:        "'Cormorant Garamond', serif",
};

/* ─── Types ───────────────────────────────────────────────── */
type Screen = "categories" | "address" | "media" | "pricing" | "description" | "settings" | "success";
type RateType = "perHour" | "perDay" | "perWeek" | "perMonth" | "perYear";
type DiscountKey = "none" | "is5" | "is10" | "is25" | "is50" | "is80";
type BookingType = "instant" | "approve";
type PolicyType  = "system" | "free" | "partial";

/* ─── Rate types ──────────────────────────────────────────── */
const RATE_TYPES: { type: RateType; label: string; desc: string; Icon: any }[] = [
  { type: "perHour",  label: "Per Hour",  desc: "Charge per hour of cleaning",     Icon: Clock    },
  { type: "perDay",   label: "Per Day",   desc: "Full day flat rate",               Icon: Calendar },
  { type: "perWeek",  label: "Per Week",  desc: "Weekly service rate",              Icon: Users    },
  { type: "perMonth", label: "Per Month", desc: "Monthly contract rate",            Icon: Sigma    },
  { type: "perYear",  label: "Per Year",  desc: "Annual service agreement",         Icon: User     },
];

const rateUnit = (t: RateType) => ({ perHour: "/hr", perDay: "/day", perWeek: "/wk", perMonth: "/mo", perYear: "/yr" }[t]);

/* ─── Discounts ───────────────────────────────────────────── */
const DISC_OPTS: { key: DiscountKey; label: string; pct: number }[] = [
  { key: "none", label: "None",  pct: 0  },
  { key: "is5",  label: "5%",   pct: 5  },
  { key: "is10", label: "10%",  pct: 10 },
  { key: "is25", label: "25%",  pct: 25 },
  { key: "is50", label: "50%",  pct: 50 },
  { key: "is80", label: "80%",  pct: 80 },
];
const buildDiscount = (k: DiscountKey) => ({ is5: k==="is5", is10: k==="is10", is25: k==="is25", is50: k==="is50", is80: k==="is80" });

/* ─── Steps config ────────────────────────────────────────── */
const STEPS: { key: Screen; label: string; desc: string }[] = [
  { key: "categories",  label: "Service Type",   desc: "What you clean"     },
  { key: "address",     label: "Location",       desc: "Service area"       },
  { key: "media",       label: "Photos & Video", desc: "Show your work"     },
  { key: "pricing",     label: "Pricing",        desc: "Rates & extras"     },
  { key: "description", label: "Description",    desc: "Your listing text"  },
  { key: "settings",    label: "Settings",       desc: "Booking & policy"   },
];
const stepIdx = (s: Screen) => STEPS.findIndex(x => x.key === s);

/* ─── CSS ─────────────────────────────────────────────────── */
const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,600;1,300&family=Montserrat:wght@400;500;600;700;800&display=swap');
*,*::before,*::after{box-sizing:border-box;margin:0;padding:0;}
html,body{background:#f0fdf4;font-family:'Montserrat',-apple-system,sans-serif;color:#14532d;}

.cf-root{min-height:100dvh;background:#f0fdf4;display:flex;flex-direction:column;}

/* desktop */
@media(min-width:900px){
  .cf-mob-bar{display:none!important;}
  .cf-layout{flex:1;display:flex;overflow:hidden;}
  .cf-sidebar{
    width:280px;flex-shrink:0;background:#14532d;
    display:flex;flex-direction:column;padding:40px 28px 32px;
    position:sticky;top:0;height:100dvh;overflow:hidden;
  }
  .cf-form-col{flex:1;overflow-y:auto;padding:44px 52px 80px;min-width:0;}
  .cf-form-col::-webkit-scrollbar{width:0;}
}

/* mobile */
@media(max-width:899px){
  .cf-sidebar{display:none!important;}
  .cf-layout{flex:1;display:flex;flex-direction:column;}
  .cf-form-col{flex:1;padding:24px 16px 72px;}
  .cf-mob-bar{
    flex-shrink:0;height:50px;background:#14532d;
    display:flex;align-items:center;padding:0 16px;gap:12px;
  }
  .cf-mob-brand{font-family:'Cormorant Garamond',serif;font-size:11px;font-weight:600;letter-spacing:5px;color:#fff;text-transform:uppercase;flex:1;}
  .cf-mob-dots{display:flex;gap:4px;}
  .cf-mob-dot{width:18px;height:2px;background:rgba(255,255,255,.2);border-radius:1px;}
  .cf-mob-dot.on{background:#4ade80;}
}

/* sidebar */
.cf-sb-brand{font-family:'Cormorant Garamond',serif;font-size:13px;font-weight:600;letter-spacing:5px;color:#fff;text-transform:uppercase;margin-bottom:4px;}
.cf-sb-sub{font-size:9px;letter-spacing:3px;color:#4ade80;font-weight:500;margin-bottom:44px;}
.cf-sb-steps{flex:1;display:flex;flex-direction:column;gap:0;}
.cf-sb-step{display:flex;align-items:flex-start;gap:12px;padding-bottom:28px;position:relative;}
.cf-sb-step::after{content:'';position:absolute;left:13px;top:28px;bottom:0;width:1px;background:rgba(255,255,255,.08);}
.cf-sb-step:last-child::after{display:none;}
.cf-sb-step:last-child{padding-bottom:0;}
.cf-indicator{width:26px;height:26px;border-radius:50%;flex-shrink:0;display:flex;align-items:center;justify-content:center;border:1px solid rgba(255,255,255,.15);font-size:10px;font-weight:800;color:rgba(255,255,255,.3);position:relative;z-index:1;}
.cf-indicator.done{background:#4ade80;border-color:#4ade80;color:#14532d;}
.cf-indicator.active{background:#fff;border-color:#fff;color:#14532d;}
.cf-sb-label{font-size:9px;font-weight:700;letter-spacing:1.5px;text-transform:uppercase;color:rgba(255,255,255,.25);padding-top:5px;}
.cf-sb-label.done,.cf-sb-label.active{color:#fff;}
.cf-sb-desc{font-size:11px;color:rgba(255,255,255,.35);margin-top:1px;}
.cf-sb-desc.done,.cf-sb-desc.active{color:rgba(255,255,255,.5);}
.cf-sb-foot{font-size:9px;letter-spacing:2px;color:rgba(255,255,255,.2);text-transform:uppercase;border-top:1px solid rgba(255,255,255,.06);padding-top:16px;margin-top:16px;}

/* fields */
.cf-field{margin-bottom:18px;}
.cf-label{font-size:8px;font-weight:700;letter-spacing:2.5px;color:#6b7280;text-transform:uppercase;margin-bottom:6px;display:block;}
.cf-input{width:100%;background:#fff;border:1px solid #bbf7d0;padding:12px 13px;font-size:13px;font-weight:500;color:#14532d;font-family:'Montserrat',sans-serif;outline:none;transition:border-color .2s;border-radius:4px;}
.cf-input:focus{border-color:#16a34a;}
.cf-input::placeholder{color:#86efac;}
.cf-textarea{width:100%;background:#fff;border:1px solid #bbf7d0;padding:12px 13px;font-size:13px;font-weight:500;color:#14532d;font-family:'Montserrat',sans-serif;outline:none;resize:none;border-radius:4px;transition:border-color .2s;}
.cf-textarea:focus{border-color:#16a34a;}
.cf-grid2{display:grid;grid-template-columns:1fr 1fr;gap:12px;}
@media(max-width:520px){.cf-grid2{grid-template-columns:1fr;}}

/* step header */
.cf-step-hd{margin-bottom:28px;}
.cf-back-btn{display:flex;align-items:center;gap:5px;background:none;border:none;cursor:pointer;color:#6b7280;font-size:11px;font-weight:600;letter-spacing:1px;font-family:'Montserrat',sans-serif;margin-bottom:20px;padding:0;transition:color .2s;}
.cf-back-btn:hover{color:#14532d;}
.cf-eyebrow{font-size:8px;font-weight:700;letter-spacing:3px;color:#16a34a;text-transform:uppercase;margin-bottom:8px;display:block;}
.cf-step-title{font-family:'Cormorant Garamond',serif;font-size:32px;font-weight:300;color:#14532d;margin-bottom:4px;line-height:1.1;}
.cf-step-sub{font-size:13px;color:#6b7280;line-height:1.6;}
.cf-divider{height:1px;background:#dcfce7;margin:24px 0;}

/* category grid */
.cf-cat-grid{display:grid;grid-template-columns:repeat(auto-fit,minmax(140px,1fr));gap:12px;margin-top:12px;}
.cf-cat-card{background:#fff;border:1.5px solid #bbf7d0;border-radius:12px;padding:16px 12px;display:flex;flex-direction:column;align-items:center;gap:8px;cursor:pointer;transition:all .18s;}
.cf-cat-card:hover{border-color:#86efac;background:#f0fdf4;}
.cf-cat-card.on{background:#14532d;border-color:#14532d;}
.cf-cat-card.on span{color:#fff!important;}
.cf-cat-card.on p{color:#4ade80!important;}
.cf-cat-icon{font-size:26px;display:flex;align-items:center;justify-content:center;}
.cf-cat-name{font-size:11px;font-weight:600;color:#14532d;text-align:center;line-height:1.3;}

/* pricing */
.cf-pricing-sec{background:#f0fdf4;border:1px solid #bbf7d0;padding:18px;margin-bottom:14px;border-radius:8px;}
.cf-pricing-title{font-size:10px;font-weight:800;letter-spacing:2px;color:#14532d;text-transform:uppercase;margin-bottom:2px;}
.cf-pricing-sub{font-size:11px;color:#6b7280;line-height:1.5;margin-bottom:14px;}
.cf-rt-grid{display:grid;grid-template-columns:1fr 1fr;gap:8px;}
@media(max-width:520px){.cf-rt-grid{grid-template-columns:1fr;}}
.cf-rt-card{display:flex;align-items:center;gap:10px;border:1px solid #bbf7d0;padding:11px 12px;cursor:pointer;background:#fff;transition:all .15s;border-radius:8px;}
.cf-rt-card:hover{border-color:#86efac;}
.cf-rt-card.on{border-color:#14532d;background:#14532d;}
.cf-rt-icon{width:30px;height:30px;background:#dcfce7;display:flex;align-items:center;justify-content:center;flex-shrink:0;border-radius:6px;transition:background .15s;}
.cf-rt-card.on .cf-rt-icon{background:rgba(74,222,128,.15);}
.cf-rt-card.on .cf-rt-icon svg{color:#4ade80!important;}
.cf-rt-label{font-size:11px;font-weight:800;color:#6b7280;letter-spacing:.3px;}
.cf-rt-card.on .cf-rt-label{color:#fff;}
.cf-rt-desc{font-size:9px;color:#6b7280;margin-top:1px;line-height:1.4;}
.cf-rt-card.on .cf-rt-desc{color:rgba(255,255,255,.4);}
.cf-rt-check{margin-left:auto;flex-shrink:0;opacity:0;transition:opacity .15s;}
.cf-rt-card.on .cf-rt-check{opacity:1;}

.cf-rate-big{display:flex;align-items:flex-end;justify-content:center;gap:4px;padding:18px 0 14px;}
.cf-rate-curr{font-size:20px;font-weight:700;color:#6b7280;padding-bottom:6px;}
.cf-rate-val{font-family:'Cormorant Garamond',serif;font-size:58px;font-weight:600;color:#14532d;line-height:1;}
.cf-rate-unit{font-size:13px;color:#6b7280;padding-bottom:8px;margin-left:4px;}
.cf-stepper{display:flex;align-items:center;gap:5px;justify-content:center;}
.cf-step-btn{background:#dcfce7;border:1px solid #bbf7d0;padding:7px 10px;cursor:pointer;font-size:10px;font-weight:700;color:#14532d;font-family:'Montserrat',sans-serif;min-width:38px;text-align:center;border-radius:6px;transition:background .15s;}
.cf-step-btn:hover{background:#bbf7d0;}
.cf-rate-input{width:76px;text-align:center;font-size:18px;font-weight:700;color:#14532d;font-family:'Cormorant Garamond',serif;border:1.5px solid #16a34a;padding:7px 0;background:#fff;outline:none;border-radius:6px;}

.cf-disc-grid{display:flex;flex-wrap:wrap;gap:7px;margin-bottom:10px;}
.cf-disc-chip{padding:7px 14px;border:1px solid #bbf7d0;background:#fff;cursor:pointer;font-size:11px;font-weight:700;color:#6b7280;font-family:'Montserrat',sans-serif;border-radius:20px;transition:all .15s;}
.cf-disc-chip:hover{border-color:#86efac;}
.cf-disc-chip.on{background:#14532d;border-color:#14532d;color:#fff;}
.cf-disc-preview{background:#dcfce7;padding:10px 14px;display:flex;flex-direction:column;gap:5px;border-radius:8px;}
.cf-disc-row{display:flex;justify-content:space-between;font-size:12px;}
.cf-disc-green{color:#16a34a;font-weight:700;}

.cf-extra-add{display:flex;gap:8px;margin-bottom:10px;}
.cf-extra-name-input{flex:1;background:#fff;border:1px solid #bbf7d0;padding:9px 12px;font-size:12px;color:#14532d;font-family:'Montserrat',sans-serif;outline:none;border-radius:6px;transition:border-color .2s;}
.cf-extra-name-input:focus{border-color:#16a34a;}
.cf-extra-price-input{width:62px;background:#fff;border:1px solid #bbf7d0;padding:9px 8px;font-size:12px;color:#14532d;font-family:'Montserrat',sans-serif;outline:none;text-align:center;border-radius:6px;}
.cf-extra-add-btn{background:#16a34a;border:none;cursor:pointer;width:36px;height:36px;display:flex;align-items:center;justify-content:center;flex-shrink:0;border-radius:8px;transition:opacity .2s;}
.cf-extra-add-btn:hover{opacity:.88;}
.cf-extra-item{display:flex;align-items:center;gap:8px;background:#dcfce7;padding:8px 12px;margin-bottom:6px;border-radius:8px;}
.cf-extra-item-name{flex:1;font-size:12px;font-weight:600;color:#14532d;}
.cf-extra-item-price{font-size:12px;font-weight:700;color:#16a34a;margin-right:6px;}
.cf-extra-del{background:none;border:none;cursor:pointer;color:#6b7280;display:flex;align-items:center;padding:2px;transition:color .2s;}
.cf-extra-del:hover{color:#dc2626;}

/* media */
.cf-upload-box{border:2px dashed #86efac;border-radius:12px;padding:36px 10px;text-align:center;cursor:pointer;color:#6b7280;margin-bottom:14px;transition:border-color .2s;}
.cf-upload-box:hover{border-color:#16a34a;}
.cf-gallery{display:flex;flex-wrap:wrap;gap:10px;}
.cf-thumb-wrap{position:relative;}
.cf-thumb{width:88px;height:88px;object-fit:cover;border-radius:10px;border:2px solid #bbf7d0;}
.cf-thumb-del{position:absolute;top:3px;right:3px;background:rgba(0,0,0,.55);border-radius:50%;padding:3px 5px;cursor:pointer;font-size:11px;color:#fff;}

/* booking cards */
.cf-bcard-grid{display:grid;grid-template-columns:repeat(auto-fit,minmax(200px,1fr));gap:10px;}
.cf-bcard{background:#fff;border:1.5px solid #bbf7d0;border-radius:12px;padding:18px;cursor:pointer;position:relative;transition:border-color .2s;}
.cf-bcard:hover{border-color:#86efac;}
.cf-bcard.on{border-color:#14532d;box-shadow:0 0 0 3px rgba(22,163,74,.1);}
.cf-bcard-badge{display:inline-block;font-size:9px;font-weight:500;background:#dcfce7;color:#16a34a;border:0.5px solid #bbf7d0;border-radius:6px;padding:2px 7px;margin-bottom:8px;letter-spacing:.04em;}
.cf-bcard-icon{width:38px;height:38px;border-radius:10px;background:#dcfce7;display:flex;align-items:center;justify-content:center;margin-bottom:10px;}
.cf-bcard-title{font-size:13px;font-weight:600;color:#14532d;margin-bottom:3px;}
.cf-bcard-desc{font-size:11px;color:#6b7280;line-height:1.5;}
.cf-check-wrap{position:absolute;top:11px;right:11px;width:20px;height:20px;border-radius:50%;background:#14532d;display:flex;align-items:center;justify-content:center;opacity:0;transition:opacity .2s;}
.cf-bcard.on .cf-check-wrap{opacity:1;}

.cf-pcard-grid{display:grid;grid-template-columns:repeat(auto-fit,minmax(160px,1fr));gap:10px;}
.cf-pcard{background:#fff;border:1.5px solid #bbf7d0;border-radius:12px;padding:14px;cursor:pointer;position:relative;transition:border-color .2s;}
.cf-pcard:hover{border-color:#86efac;}
.cf-pcard.on{border-color:#14532d;box-shadow:0 0 0 3px rgba(22,163,74,.1);}
.cf-pcard-icon{width:32px;height:32px;border-radius:8px;display:flex;align-items:center;justify-content:center;margin-bottom:8px;}
.cf-pcard-title{font-size:12px;font-weight:600;color:#14532d;margin-bottom:2px;}
.cf-pcard-desc{font-size:10px;color:#6b7280;line-height:1.4;}

/* buttons */
.cf-primary{width:100%;padding:15px 0;background:#14532d;border:none;cursor:pointer;color:#fff;font-size:11px;font-weight:700;letter-spacing:2.5px;text-transform:uppercase;font-family:'Montserrat',sans-serif;display:flex;align-items:center;justify-content:center;gap:8px;transition:opacity .2s;margin-top:8px;border-radius:10px;}
.cf-primary:hover{opacity:.88;}
.cf-primary:disabled{background:#86efac;cursor:not-allowed;color:#14532d;opacity:1;}

/* error */
.cf-error{background:#fef2f2;border:1px solid #fecaca;padding:11px 14px;margin-bottom:12px;font-size:12px;color:#dc2626;display:flex;align-items:center;gap:8px;border-radius:8px;}

/* overlay */
.cf-overlay{position:fixed;inset:0;background:rgba(0,0,0,.5);backdropFilter:blur(6px);display:flex;align-items:center;justify-content:center;z-index:200;}

/* success */
.cf-success-page{min-height:100dvh;background:#14532d;display:flex;align-items:center;justify-content:center;padding:40px 24px;}
.cf-success-inner{max-width:480px;width:100%;text-align:center;}
.cf-success-ring{width:88px;height:88px;border-radius:50%;border:2px solid rgba(74,222,128,.4);display:flex;align-items:center;justify-content:center;margin:0 auto 28px;}
.cf-success-title{font-family:'Cormorant Garamond',serif;font-size:34px;font-weight:300;color:#fff;margin-bottom:12px;line-height:1.15;}
.cf-success-sub{font-size:13px;color:rgba(255,255,255,.5);line-height:1.8;margin-bottom:36px;}
.cf-spin{animation:cfSpin .8s linear infinite;}
@keyframes cfSpin{to{transform:rotate(360deg);}}
::-webkit-scrollbar{width:0;height:0;}
`;

/* ─── Sub-components ──────────────────────────────────────── */
function StepHeader({ idx, title, sub, onBack }: { idx: number; title: string; sub: string; onBack: () => void }) {
  return (
    <div className="cf-step-hd">
      <button className="cf-back-btn" onClick={onBack}><ChevronLeft size={13} /> Back</button>
      <span className="cf-eyebrow">Step {idx + 1} of {STEPS.length}</span>
      <div className="cf-step-title">{title}</div>
      <div className="cf-step-sub">{sub}</div>
    </div>
  );
}

function Err({ text }: { text: string }) {
  return <div className="cf-error"><AlertCircle size={13} />{text}</div>;
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return <div className="cf-field"><label className="cf-label">{label}</label>{children}</div>;
}

/* ─── Sidebar ─────────────────────────────────────────────── */
function Sidebar({ current }: { current: Screen }) {
  const ci = stepIdx(current);
  return (
    <div className="cf-sidebar">
   
      <div className="cf-sb-sub">Cleaning Services</div>
      <div className="cf-sb-steps">
        {STEPS.map((step, i) => {
          const isDone   = i < ci;
          const isActive = i === ci;
          const cls = isDone ? "done" : isActive ? "active" : "";
          return (
            <div key={step.key} className="cf-sb-step">
              <div className={`cf-indicator${cls ? ` ${cls}` : ""}`}>
                {isDone ? <Check size={12} /> : i + 1}
              </div>
              <div>
                <div className={`cf-sb-label${cls ? ` ${cls}` : ""}`}>{step.label}</div>
                <div className={`cf-sb-desc${cls ? ` ${cls}` : ""}`}>
                  {isDone ? "Complete" : isActive ? "In progress" : step.desc}
                </div>
              </div>
            </div>
          );
        })}
      </div>
      <div className="cf-sb-foot">Areluxe · {new Date().getFullYear()}</div>
    </div>
  );
}

function MobileBar({ current }: { current: Screen }) {
  const ci = stepIdx(current);
  return (
    <div className="cf-mob-bar">
      <span className="cf-mob-brand">Areluxe</span>
      <div className="cf-mob-dots">
        {STEPS.map((_, i) => (
          <div key={i} className={`cf-mob-dot${i <= ci ? " on" : ""}`} />
        ))}
      </div>
    </div>
  );
}

/* ═════════════════════════════════════════════════════════════
   MAIN
═════════════════════════════════════════════════════════════ */
export default function CleaningForm() {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { userId, listingId: paramListingId } = useParams<{ userId: string; listingId?: string }>();
  const user         = useSelector((s: RootState) => s.auth.user);
  const currentListing = useSelector((s: RootState) => s.cleaning.currentListing);

  /* inject CSS once */
  useEffect(() => {
    const id = "cf-css";
    if (!document.getElementById(id)) {
      const tag = document.createElement("style"); tag.id = id; tag.textContent = CSS;
      document.head.appendChild(tag);
    }
  }, []);

  const [screen,    setScreen]    = useState<Screen>("categories");
  const [listingId, setListingId] = useState<string | undefined>(paramListingId);
  const [loading,   setLoading]   = useState(false);
  const [error,     setError]     = useState<string | null>(null);
  const [saving,    setSaving]    = useState(false);
  const [saved,     setSaved]     = useState(false);

  /* load existing listing */
  useEffect(() => {
    if (listingId) dispatch(listenToCleaningData(listingId));
  }, [listingId, dispatch]);

  /* ─── STEP 1: categories ─── */
 const [selectedCategory,  setSelectedCategory]  = useState<number | null>(null);
const [selectedAmenities, setSelectedAmenities] = useState<number[]>([]);
const [selectedSafety,    setSelectedSafety]    = useState<number[]>([]);
const [selectedRules,     setSelectedRules]     = useState<number[]>([]);
  useEffect(() => {
    if (currentListing) {
      setSelectedCategory(currentListing.category ?? null);
    }
  }, [currentListing]);

  /* ─── STEP 2: address ─── */
  const streetRef = useRef<HTMLInputElement>(null);
  const [address, setAddress] = useState({
    country: "", street: "", neighborhood: "", apt: "", zip: "", city: "", county: "",
    latitude: "", longitude: "",
  });

  useEffect(() => {
    if (currentListing?.address) {
      const a = currentListing.address;
      setAddress({
        country:      a.country      ?? "",
        street:       a.street       ?? "",
        neighborhood: a.neighborhood ?? "",
        apt:          a.apt          ?? "",
        zip:          a.zip          ?? "",
        city:         a.city         ?? "",
        county:       a.county       ?? "",
        latitude:     a.latitude  != null ? String(a.latitude)  : "",
        longitude:    a.longitude != null ? String(a.longitude) : "",
      });
    }
  }, [currentListing]);

  /* Google Autocomplete */
  useEffect(() => {
    if (!window.google?.maps?.places || !streetRef.current) return;
    const ac = new window.google.maps.places.Autocomplete(streetRef.current, { types: ["address"] });
    ac.addListener("place_changed", () => {
      const place = ac.getPlace();
      const get = (type: string) => place.address_components?.find((c: any) => c.types.includes(type))?.long_name || "";
      setAddress(p => ({
        ...p,
        street:       [get("street_number"), get("route")].filter(Boolean).join(" ") || p.street,
        city:         get("locality") || get("administrative_area_level_2"),
        neighborhood: get("sublocality"),
        country:      get("country"),
        county:       get("administrative_area_level_1"),
        zip:          get("postal_code"),
        latitude:     place.geometry?.location?.lat().toString() || "",
        longitude:    place.geometry?.location?.lng().toString() || "",
      }));
    });
  }, [screen]);

  const useCurrentLocation = () => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition((pos) => {
      const lat = pos.coords.latitude.toString();
      const lng = pos.coords.longitude.toString();
      if (window.google?.maps) {
        const geocoder = new window.google.maps.Geocoder();
        geocoder.geocode({ location: { lat: +lat, lng: +lng } }, (results: any, status: any) => {
          if (status === "OK" && results?.[0]) {
            const get = (type: string) => results[0].address_components?.find((c: any) => c.types.includes(type))?.long_name || "";
            setAddress(p => ({
              ...p,
              street:       [get("street_number"), get("route")].filter(Boolean).join(" ") || p.street,
              city:         get("locality") || get("administrative_area_level_2"),
              neighborhood: get("sublocality"),
              country:      get("country"),
              county:       get("administrative_area_level_1"),
              zip:          get("postal_code"),
              latitude:     lat, longitude: lng,
            }));
          }
        });
      } else {
        setAddress(p => ({ ...p, latitude: lat, longitude: lng }));
      }
    });
  };

  /* ─── STEP 3: media ─── */
  const photoInputRef = useRef<HTMLInputElement>(null);
  const videoInputRef = useRef<HTMLInputElement>(null);
  const [photos,       setPhotos]       = useState<File[]>([]);
  const [photoUrls,    setPhotoUrls]    = useState<string[]>([]);
  const [videoFile,    setVideoFile]    = useState<File | null>(null);
  const [videoUrl,     setVideoUrl]     = useState("");
  const [videoPreview, setVideoPreview] = useState("");
  const [uploadErr,    setUploadErr]    = useState<string | null>(null);

  useEffect(() => {
    if (currentListing) {
      setPhotoUrls(currentListing.photos || []);
      setVideoUrl(currentListing.videoUrl || "");
    }
  }, [currentListing]);

  const handleAddPhotos = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return;
    const files = Array.from(e.target.files);
    if (photoUrls.length + photos.length + files.length > 10) { setUploadErr("Max 10 photos."); return; }
    setPhotos(p => [...p, ...files]);
    setUploadErr(null);
  };

  const handleVideoSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && file.type.startsWith("video/")) {
      setVideoFile(file);
      setVideoPreview(URL.createObjectURL(file));
    }
  };

  const handleMediaUpload = async () => {
    if (!listingId) return;
    setSaving(true); setUploadErr(null);
    try {
      if (photos.length) {
        await Promise.all(photos.map(file =>
          dispatch(upsertCleaningMedia({ files: [file], listingId, type: "photo" })).unwrap()
        ));
        setPhotos([]);
      }
      if (videoFile) {
        const vid = document.createElement("video");
        vid.src = URL.createObjectURL(videoFile);
        await new Promise(r => { vid.onloadedmetadata = r; });
        if (vid.duration > 60) { setUploadErr("Video max 60 seconds."); setSaving(false); return; }
        await dispatch(upsertCleaningMedia({ files: [videoFile], listingId, type: "video" })).unwrap();
        setVideoFile(null); setVideoPreview("");
      }
      setSaved(true); setTimeout(() => setSaved(false), 2000);
    } catch { setUploadErr("Upload failed. Try again."); }
    finally { setSaving(false); }
  };

  /* ─── STEP 4: pricing ─── */
  const [rateType,    setRateType]    = useState<RateType>("perHour");
  const [rate,        setRate]        = useState(20);
  const [discountKey, setDiscountKey] = useState<DiscountKey>("none");
  const [extras,      setExtras]      = useState<{ name: string; price: number }[]>([]);
  const [newExName,   setNewExName]   = useState("");
  const [newExPrice,  setNewExPrice]  = useState("");

  useEffect(() => {
    if (currentListing) {
      if (currentListing.rate)     setRate(currentListing.rate);
      if (currentListing.rateType) setRateType(currentListing.rateType as RateType);
      if (currentListing.extras) {
        setExtras(Object.entries(currentListing.extras).map(([n, p]) => ({ name: n, price: Number(p) })));
      }
    }
  }, [currentListing]);

  const addExtra = () => {
    const name = newExName.trim(); const price = parseFloat(newExPrice);
    if (!name || !price || price <= 0) return;
    if (extras.some(e => e.name.toLowerCase() === name.toLowerCase())) return;
    setExtras(p => [...p, { name, price }]);
    setNewExName(""); setNewExPrice("");
  };

  const adjustRate = (step: number) => setRate(p => Math.max(1, Math.min(100000, p + step)));
  const discObj     = DISC_OPTS.find(d => d.key === discountKey)!;
  const previewRate = rate * (1 - discObj.pct / 100);
  const extraCharges= extras.reduce((s, e) => s + e.price, 0);
  const guestFee    = calculateCleaningGuestServiceFee(previewRate);

  /* ─── STEP 5: description ─── */
  const [title,        setTitle]        = useState("");
  const [description,  setDescription]  = useState("");
  const [neighborhood2, setNeighborhood2] = useState("");

  useEffect(() => {
    if (currentListing) {
      setTitle(currentListing.name || currentListing.title || "");
      setDescription(currentListing.description || "");
      setNeighborhood2(currentListing.neighborhood || "");
    }
  }, [currentListing]);

  /* ─── STEP 6: settings ─── */
  const [bookingType, setBookingType] = useState<BookingType>("instant");
  const [policy,      setPolicy]      = useState<PolicyType>("system");
  const [showModal,   setShowModal]   = useState(false);

  useEffect(() => {
    if (currentListing) {
      const bt = currentListing.bookingType;
      setBookingType(bt?.approveReservations ? "approve" : "instant");
      const cp = currentListing.cancellationPolicy;
      if (cp?.free)         setPolicy("free");
      else if (cp?.partial) setPolicy("partial");
      else                  setPolicy("system");
    }
  }, [currentListing]);

  /* ─── Navigation ──────────────────────────────────────────── */
  const goTo = (next: Screen) => { setScreen(next); setError(null); window.scrollTo(0, 0); };

  const ensureListing = async (data: any): Promise<string | null> => {
    const uid = user?.userId;
    if (!uid) { setError("Sign in first."); return null; }
    try {
      const result = await dispatch(upsertCleaning({ listingId, data })).unwrap();
      const id = result.listingId;
      if (!listingId) setListingId(id);
      return id;
    } catch (e: any) { setError(e?.message ?? "Failed to save."); return null; }
  };

  /* ── Save each step ── */
  const saveCategories = async () => {
    if (selectedCategory === null) { setError("Select a service type."); return; }
    setLoading(true);
const id = await ensureListing({ category: selectedCategory, name: "Untitled Cleaning Listing", amenities: selectedAmenities, safety: selectedSafety, rules: selectedRules });
    setLoading(false);
    if (id) goTo("address");
  };

  const saveAddress = async () => {
    if (!address.street || !address.city) { setError("Street and city are required."); return; }
    setLoading(true);
    await ensureListing({
      address: {
        ...address,
        latitude:  address.latitude  ? Number(address.latitude)  : null,
        longitude: address.longitude ? Number(address.longitude) : null,
      },
    });
    setLoading(false);
    if (!error) goTo("media");
  };

  const saveMedia = () => goTo("pricing"); // media saved via upload button

  const savePricing = async () => {
    setLoading(true);
    const extrasObj: Record<string, number> = {};
    extras.forEach(e => { extrasObj[e.name] = e.price; });
    await ensureListing({
      rate, rateType,
      extras: extrasObj, extraCharges,
      discountApplied: buildDiscount(discountKey),
    });
    setLoading(false);
    if (!error) goTo("description");
  };

  const saveDescription = async () => {
    if (!title.trim()) { setError("Title is required."); return; }
    setLoading(true);
    await ensureListing({ name: title, title, description, neighborhood: neighborhood2 });
    setLoading(false);
    if (!error) goTo("settings");
  };

  const saveSettings = async () => {
    setLoading(true);
    await ensureListing({
      bookingType: { instantReservations: bookingType === "instant", approveReservations: bookingType === "approve" },
      cancellationPolicy: { free: policy === "free", partial: policy === "partial", system: policy === "system" },
      inReview: true, isDraft: false,
    });
    setLoading(false);
    if (!error) goTo("success");
  };

  /* ═════════════════════ RENDER ═════════════════════════════ */

  /* SUCCESS */
  if (screen === "success") {
    return (
      <div className="cf-root">
        <style>{CSS}</style>
        <div className="cf-success-page">
          <div className="cf-success-inner">
            <div className="cf-success-ring"><Check size={36} color="#4ade80" /></div>
            <div className="cf-success-title">Listing Submitted</div>
            <div className="cf-success-sub">
              Your cleaning listing is under review. We'll notify you once it goes live — then you can start receiving bookings.
            </div>
            <button className="cf-primary" style={{ maxWidth: 260, margin: "0 auto", borderRadius: 10 }}
              onClick={() => navigate(`/cleaningdashboard/${userId}`)}>
              Go to Dashboard <ArrowRight size={14} />
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="cf-root">
      <style>{CSS}</style>
      <MobileBar current={screen} />

      {/* overlays */}
      {(saving || saved) && (
        <div className="cf-overlay">
          {saving && !saved && <CircularProgress size={60} sx={{ color: "#4ade80" }} />}
          {saved  && <CheckCircleOutlineIcon sx={{ fontSize: 76, color: "#4ade80" }} />}
        </div>
      )}
      {showModal && (
        <div className="cf-overlay">
          <div style={{ background: G.surface, borderRadius: 16, padding: 28, maxWidth: 380, width: "100%", border: `1px solid ${G.border}` }}>
            <div style={{ width: 46, height: 46, borderRadius: "50%", background: "#fef9c3", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 14 }}>
              <AlertCircle size={20} color="#ca8a04" />
            </div>
            <p style={{ fontSize: 15, fontWeight: 600, color: G.text, marginBottom: 8 }}>Instant booking is recommended</p>
            <p style={{ fontSize: 12, color: G.muted, lineHeight: 1.6, marginBottom: 14 }}>
              Listings with instant booking get more visibility and convert at a higher rate. Are you sure you want manual approval?
            </p>
            <div style={{ display: "flex", gap: 10 }}>
              <button onClick={() => setShowModal(false)} style={{ flex: 1, padding: "10px", borderRadius: 8, background: "transparent", color: G.muted, border: `1px solid ${G.border2}`, cursor: "pointer", fontFamily: G.sans, fontSize: 13 }}>Keep instant</button>
              <button onClick={() => { setBookingType("approve"); setShowModal(false); }} style={{ flex: 1, padding: "10px", borderRadius: 8, background: G.greenDark, color: "#fff", border: "none", cursor: "pointer", fontFamily: G.sans, fontSize: 13, fontWeight: 600 }}>Use approval</button>
            </div>
          </div>
        </div>
      )}

      <div className="cf-layout">
        <Sidebar current={screen} />

        <div className="cf-form-col">

          {/* ══════ STEP 1: CATEGORIES ══════ */}
          {screen === "categories" && (
            <>
              <StepHeader idx={0} title="Service Type" sub="What kind of cleaning do you offer?" onBack={() => navigate(-1)} />
              <div className="cf-cat-grid">
                {cleaningCategories.map(item => {
                  const on = selectedCategory === item.id;
                  return (
                    <div key={item.id} className={`cf-cat-card${on ? " on" : ""}`} onClick={() => setSelectedCategory(on ? null : item.id)}>
                      <span className="cf-cat-icon" style={{ color: on ? "#4ade80" : G.green }}>{item.icon}</span>
                      <p className="cf-cat-name">{item.name}</p>
                    </div>
                  );
                })}
              </div>


              {/* ── Amenities ── */}
              <div style={{ marginTop: 32 }}>
                <div className="cf-pricing-title" style={{ marginBottom: 4 }}>What's Included</div>
                <div className="cf-pricing-sub" style={{ marginBottom: 14, fontSize: 11, color: "#6b7280" }}>Select all the services and areas you cover.</div>
                <div className="cf-cat-grid">
                  {cleaningAmenities.map(item => {
                    const on = selectedAmenities.includes(item.id);
                    return (
                      <div key={item.id} className={`cf-cat-card${on ? " on" : ""}`}
                        onClick={() => setSelectedAmenities(prev => on ? prev.filter(x => x !== item.id) : [...prev, item.id])}>
                        <span className="cf-cat-icon" style={{ color: on ? "#4ade80" : "#16a34a" }}>{item.icon}</span>
                        <p className="cf-cat-name">{item.name}</p>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* ── Safety ── */}
              <div style={{ marginTop: 32 }}>
                <div className="cf-pricing-title" style={{ marginBottom: 4 }}>Safety & Standards</div>
                <div className="cf-pricing-sub" style={{ marginBottom: 14, fontSize: 11, color: "#6b7280" }}>Let clients know your safety practices.</div>
                <div className="cf-cat-grid">
                  {cleaningSafetyCategories.map(item => {
                    const on = selectedSafety.includes(item.id);
                    return (
                      <div key={item.id} className={`cf-cat-card${on ? " on" : ""}`}
                        onClick={() => setSelectedSafety(prev => on ? prev.filter(x => x !== item.id) : [...prev, item.id])}>
                        <span className="cf-cat-icon" style={{ color: on ? "#4ade80" : "#16a34a" }}>{item.icon}</span>
                        <p className="cf-cat-name">{item.name}</p>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* ── Rules ── */}
              <div style={{ marginTop: 32, marginBottom: 8 }}>
                <div className="cf-pricing-title" style={{ marginBottom: 4 }}>Rules & Preferences</div>
                <div className="cf-pricing-sub" style={{ marginBottom: 14, fontSize: 11, color: "#6b7280" }}>Set expectations for how your service works.</div>
                <div className="cf-cat-grid">
                  {cleaningRulesCategories.map(item => {
                    const on = selectedRules.includes(item.id);
                    return (
                      <div key={item.id} className={`cf-cat-card${on ? " on" : ""}`}
                        onClick={() => setSelectedRules(prev => on ? prev.filter(x => x !== item.id) : [...prev, item.id])}>
                        <span className="cf-cat-icon" style={{ color: on ? "#4ade80" : "#16a34a" }}>{item.icon}</span>
                        <p className="cf-cat-name">{item.name}</p>
                      </div>
                    );
                  })}
                </div>
              </div>

              {error && <><div style={{ marginTop: 16 }} /><Err text={error} /></>}
              <button className="cf-primary" style={{ marginTop: 24 }} disabled={loading || selectedCategory === null} onClick={saveCategories}>
                {loading ? <RefreshCw size={15} className="cf-spin" /> : <>Continue <ArrowRight size={14} /></>}
              </button>
            </>
          )}

          {/* ══════ STEP 2: ADDRESS ══════ */}
          {screen === "address" && (
            <>
              <StepHeader idx={1} title="Service Location" sub="Where is your cleaning service based?" onBack={() => goTo("categories")} />
              <Field label="Street address *">
                <input ref={streetRef} className="cf-input" value={address.street} onChange={e => setAddress(p => ({ ...p, street: e.target.value }))} placeholder="123 Main Street" />
              </Field>
              <Field label="Country *">
                <input className="cf-input" value={address.country} onChange={e => setAddress(p => ({ ...p, country: e.target.value }))} placeholder="United Kingdom" />
              </Field>
              <div className="cf-grid2">
                <Field label="City *"><input className="cf-input" value={address.city} onChange={e => setAddress(p => ({ ...p, city: e.target.value }))} placeholder="London" /></Field>
                <Field label="County / State"><input className="cf-input" value={address.county} onChange={e => setAddress(p => ({ ...p, county: e.target.value }))} placeholder="Greater London" /></Field>
              </div>
              <div className="cf-grid2">
                <Field label="Apt / Unit"><input className="cf-input" value={address.apt} onChange={e => setAddress(p => ({ ...p, apt: e.target.value }))} placeholder="Unit 4B" /></Field>
                <Field label="ZIP / Postcode"><input className="cf-input" value={address.zip} onChange={e => setAddress(p => ({ ...p, zip: e.target.value }))} placeholder="SW1A 1AA" /></Field>
              </div>
              <Field label="Neighbourhood">
                <input className="cf-input" value={address.neighborhood} onChange={e => setAddress(p => ({ ...p, neighborhood: e.target.value }))} placeholder="Chelsea" />
              </Field>
              <button
                onClick={useCurrentLocation}
                style={{ display: "flex", alignItems: "center", gap: 8, background: G.greenLight, border: `1px solid ${G.border}`, borderRadius: 8, padding: "9px 16px", cursor: "pointer", fontSize: 12, fontWeight: 600, color: G.text, fontFamily: G.sans, marginTop: 4 }}
              >
                <MapPin size={14} color={G.green} /> Use current location
              </button>
              {error && <><div style={{ marginTop: 12 }} /><Err text={error} /></>}
              <button className="cf-primary" style={{ marginTop: 20 }} disabled={loading} onClick={saveAddress}>
                {loading ? <RefreshCw size={15} className="cf-spin" /> : <>Continue <ArrowRight size={14} /></>}
              </button>
            </>
          )}

          {/* ══════ STEP 3: MEDIA ══════ */}
          {screen === "media" && (
            <>
              <StepHeader idx={2} title="Photos & Video" sub="Show clients what you do — at least 3 photos required." onBack={() => goTo("address")} />

              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 20 }}>
                {/* photos */}
                <div style={{ background: G.bg, border: `1px solid ${G.border}`, borderRadius: 12, padding: 18 }}>
                  <p style={{ fontSize: 13, fontWeight: 700, color: G.text, marginBottom: 10 }}>Photos</p>
                  <div className="cf-upload-box" onClick={() => photoInputRef.current?.click()}>
                    <span style={{ fontSize: 40 }}>📷</span>
                    <p style={{ marginTop: 8, fontSize: 12 }}>Click to add photos</p>
                  </div>
                  <input ref={photoInputRef} type="file" multiple accept="image/*" onChange={handleAddPhotos} style={{ display: "none" }} />
                  <div className="cf-gallery">
                    {photoUrls.map((url, i) => (
                      <div key={`u-${i}`} className="cf-thumb-wrap">
                        <img src={url} className="cf-thumb" alt="" />
                        <span className="cf-thumb-del" onClick={() => setPhotoUrls(p => p.filter((_, j) => j !== i))}>✕</span>
                      </div>
                    ))}
                    {photos.map((f, i) => (
                      <div key={`f-${i}`} className="cf-thumb-wrap">
                        <img src={URL.createObjectURL(f)} className="cf-thumb" alt="" />
                        <span className="cf-thumb-del" onClick={() => setPhotos(p => p.filter((_, j) => j !== i))}>✕</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* video */}
                <div style={{ background: G.bg, border: `1px solid ${G.border}`, borderRadius: 12, padding: 18 }}>
                  <p style={{ fontSize: 13, fontWeight: 700, color: G.text, marginBottom: 10 }}>Intro Video (max 60s)</p>
                  {!videoPreview && !videoUrl ? (
                    <div className="cf-upload-box" onClick={() => videoInputRef.current?.click()}>
                      <span style={{ fontSize: 40 }}>🎞️</span>
                      <p style={{ marginTop: 8, fontSize: 12 }}>Click to add video</p>
                    </div>
                  ) : (
                    <div style={{ position: "relative" }}>
                      <video src={videoPreview || videoUrl} controls style={{ width: "100%", borderRadius: 10, border: `2px solid ${G.border}` }} />
                      <button onClick={() => videoInputRef.current?.click()}
                        style={{ position: "absolute", top: 8, left: 8, background: G.green, border: "none", borderRadius: 16, padding: "4px 10px", color: "#fff", fontSize: 11, fontWeight: 700, cursor: "pointer" }}>
                        Replace
                      </button>
                    </div>
                  )}
                  <input ref={videoInputRef} type="file" accept="video/*" onChange={handleVideoSelect} style={{ display: "none" }} />
                </div>
              </div>

              {uploadErr && <><div style={{ marginTop: 12 }} /><Err text={uploadErr} /></>}

              <button
                onClick={handleMediaUpload}
                disabled={saving || (photos.length === 0 && !videoFile)}
                style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, background: G.green, border: "none", borderRadius: 10, padding: "12px 24px", color: "#fff", fontWeight: 700, fontSize: 12, cursor: "pointer", marginTop: 20, opacity: (photos.length === 0 && !videoFile) ? 0.5 : 1, fontFamily: G.sans }}
              >
                {saving ? <RefreshCw size={14} className="cf-spin" /> : "Save Media"}
              </button>

              <button
                className="cf-primary"
                style={{ marginTop: 10 }}
                disabled={photoUrls.length < 3}
                onClick={() => goTo("pricing")}
              >
                Continue <ArrowRight size={14} />
              </button>
              {photoUrls.length < 3 && (
                <p style={{ fontSize: 11, color: G.muted, textAlign: "center", marginTop: 6 }}>Upload & save at least 3 photos to continue</p>
              )}
            </>
          )}

          {/* ══════ STEP 4: PRICING ══════ */}
          {screen === "pricing" && (
            <>
              <StepHeader idx={3} title="Pricing" sub="Set your rate and billing method." onBack={() => goTo("media")} />

              {/* rate type */}
              <div className="cf-pricing-sec">
                <div className="cf-pricing-title">Billing Method</div>
                <div className="cf-pricing-sub">How clients are charged for your service.</div>
                <div className="cf-rt-grid">
                  {RATE_TYPES.map(({ type, label, desc, Icon }) => (
                    <div key={type} className={`cf-rt-card${rateType === type ? " on" : ""}`} onClick={() => setRateType(type)}>
                      <div className="cf-rt-icon"><Icon size={14} /></div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div className="cf-rt-label">{label}</div>
                        <div className="cf-rt-desc">{desc}</div>
                      </div>
                      <div className="cf-rt-check"><Check size={12} color={rateType === type ? "#4ade80" : "transparent"} /></div>
                    </div>
                  ))}
                </div>
              </div>

              {/* base rate */}
              <div className="cf-pricing-sec">
                <div className="cf-pricing-title">Base Rate</div>
                <div className="cf-pricing-sub">Your fee before extras and discounts.</div>
                <div className="cf-rate-big">
                  <span className="cf-rate-curr">€</span>
                  <span className="cf-rate-val">{rate}</span>
                  <span className="cf-rate-unit">{rateUnit(rateType)}</span>
                </div>
                <div className="cf-stepper">
                  {[-50, -10, -1].map(s => <button key={s} className="cf-step-btn" onClick={() => adjustRate(s)}>{s}</button>)}
                  <input className="cf-rate-input" value={rate} onChange={e => setRate(Math.max(1, parseInt(e.target.value) || 1))} />
                  {[+1, +10, +50].map(s => <button key={s} className="cf-step-btn" onClick={() => adjustRate(s)}>+{s}</button>)}
                </div>
                <p style={{ textAlign: "center", fontSize: 11, color: G.muted, marginTop: 10 }}>
                  Guest fee: +€{guestFee.toFixed(2)} · Total to client: €{(previewRate + guestFee).toFixed(2)} {rateUnit(rateType)}
                </p>
              </div>

              {/* discount */}
              <div className="cf-pricing-sec">
                <div className="cf-pricing-title">Default Discount</div>
                <div className="cf-pricing-sub">Applied automatically at checkout.</div>
                <div className="cf-disc-grid">
                  {DISC_OPTS.map(d => (
                    <button key={d.key} className={`cf-disc-chip${discountKey === d.key ? " on" : ""}`} onClick={() => setDiscountKey(d.key)}>{d.label}</button>
                  ))}
                </div>
                {discObj.pct > 0 && (
                  <div className="cf-disc-preview">
                    <div className="cf-disc-row"><span>Original</span><span>€{rate} {rateUnit(rateType)}</span></div>
                    <div className="cf-disc-row"><span>After {discObj.label}</span><span className="cf-disc-green">€{previewRate.toFixed(2)} {rateUnit(rateType)}</span></div>
                  </div>
                )}
              </div>

              {/* extras */}
              <div className="cf-pricing-sec">
                <div className="cf-pricing-title">Extra Charges {extraCharges > 0 ? `· €${extraCharges} total` : ""}</div>
                <div className="cf-pricing-sub">Optional add-ons (supplies, deep clean fee, etc.)</div>
                <div className="cf-extra-add">
                  <input className="cf-extra-name-input" placeholder="e.g. Oven cleaning, Supplies…" value={newExName} onChange={e => setNewExName(e.target.value)} onKeyDown={e => e.key === "Enter" && addExtra()} />
                  <input className="cf-extra-price-input" placeholder="€" value={newExPrice} onChange={e => setNewExPrice(e.target.value)} onKeyDown={e => e.key === "Enter" && addExtra()} />
                  <button className="cf-extra-add-btn" onClick={addExtra}><Plus size={14} color="#fff" /></button>
                </div>
                {extras.map((item, i) => (
                  <div key={i} className="cf-extra-item">
                    <span className="cf-extra-item-name">{item.name}</span>
                    <span className="cf-extra-item-price">€{item.price}</span>
                    <button className="cf-extra-del" onClick={() => setExtras(p => p.filter((_, j) => j !== i))}><X size={13} /></button>
                  </div>
                ))}
              </div>

              {error && <Err text={error} />}
              <button className="cf-primary" disabled={loading} onClick={savePricing}>
                {loading ? <RefreshCw size={15} className="cf-spin" /> : <>Continue <ArrowRight size={14} /></>}
              </button>
            </>
          )}

          {/* ══════ STEP 5: DESCRIPTION ══════ */}
          {screen === "description" && (
            <>
              <StepHeader idx={4} title="Describe Your Service" sub="Help clients understand what makes you stand out." onBack={() => goTo("pricing")} />
              <Field label="Listing Title *">
                <input className="cf-input" value={title} onChange={e => setTitle(e.target.value)} placeholder="Professional home cleaning — London" />
              </Field>
              <Field label="Description">
                <textarea className="cf-textarea" rows={5} value={description} onChange={e => setDescription(e.target.value)} placeholder="Tell clients about your experience, what's included, how you work, and why they should choose you…" />
              </Field>
              <Field label="Neighbourhood / Area notes">
                <input className="cf-input" value={neighborhood2} onChange={e => setNeighborhood2(e.target.value)} placeholder="We cover all of Zone 1–3 in London" />
              </Field>
              {error && <Err text={error} />}
              <button className="cf-primary" disabled={loading} onClick={saveDescription}>
                {loading ? <RefreshCw size={15} className="cf-spin" /> : <>Continue <ArrowRight size={14} /></>}
              </button>
            </>
          )}

          {/* ══════ STEP 6: SETTINGS ══════ */}
          {screen === "settings" && (
            <>
              <StepHeader idx={5} title="Booking Settings" sub="Choose how clients book and your cancellation policy." onBack={() => goTo("description")} />

              <p style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.1em", color: G.muted, textTransform: "uppercase" as const, marginBottom: 12 }}>Booking type</p>
              <div className="cf-bcard-grid">
                {[
                  { id: "instant" as BookingType, title: "Instant booking", desc: "Clients book immediately — no approval needed.", icon: <Zap size={18} color={G.muted} />, recommended: true },
                  { id: "approve" as BookingType, title: "Approve bookings", desc: "Review each request before confirming.", icon: <UserCheck size={18} color={G.muted} /> },
                ].map(({ id, title: t, desc, icon, recommended }) => (
                  <div key={id} className={`cf-bcard${bookingType === id ? " on" : ""}`}
                    onClick={() => id === "approve" ? setShowModal(true) : setBookingType("instant")}>
                    {recommended && <span className="cf-bcard-badge">Recommended</span>}
                    <div className="cf-check-wrap"><CheckCircleOutlineIcon sx={{ fontSize: 13, color: "#fff" }} /></div>
                    <div className="cf-bcard-icon">{icon}</div>
                    <p className="cf-bcard-title">{t}</p>
                    <p className="cf-bcard-desc">{desc}</p>
                  </div>
                ))}
              </div>

              <p style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.1em", color: G.muted, textTransform: "uppercase" as const, margin: "24px 0 12px" }}>Cancellation policy</p>
              <div className="cf-pcard-grid">
                {[
                  { id: "system" as PolicyType, title: "System default", desc: "Whoever cancels compensates one session's rate.", icon: <Settings2 size={15} color={G.muted} />, iconBg: G.bg },
                  { id: "free"   as PolicyType, title: "Free cancellation", desc: "Client receives a full refund if they cancel.",   icon: <ShieldCheck size={15} color="#16a34a" />, iconBg: G.greenLight },
                  { id: "partial"as PolicyType, title: "Partial (50%)", desc: "Client and cleaner each keep 50% of the booking.", icon: <DollarSign size={15} color="#ca8a04" />, iconBg: "#fef9c3" },
                ].map(({ id, title: t, desc, icon, iconBg }) => (
                  <div key={id} className={`cf-pcard${policy === id ? " on" : ""}`} onClick={() => setPolicy(id)}>
                    <div className="cf-check-wrap"><CheckCircleOutlineIcon sx={{ fontSize: 12, color: "#fff" }} /></div>
                    <div className="cf-pcard-icon" style={{ background: iconBg }}>{icon}</div>
                    <p className="cf-pcard-title">{t}</p>
                    <p className="cf-pcard-desc">{desc}</p>
                  </div>
                ))}
              </div>

              {error && <><div style={{ marginTop: 12 }} /><Err text={error} /></>}
              <button className="cf-primary" style={{ marginTop: 24 }} disabled={loading} onClick={saveSettings}>
                {loading ? <RefreshCw size={15} className="cf-spin" /> : <>Submit Listing <ArrowRight size={14} /></>}
              </button>
            </>
          )}

        </div>
      </div>
    </div>
  );
}
